/**
 * Centralized Logger Utility
 *
 * Provides environment-aware logging that can be disabled in production.
 * All console output should go through this utility.
 */

const isDevelopment = import.meta.env.DEV;

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LoggerOptions {
  /** Force logging even in production (use sparingly) */
  force?: boolean;
  /** Additional context to include */
  context?: Record<string, unknown>;
}

/**
 * Development-only logger that silences output in production
 */
export const logger = {
  /**
   * Debug level - only in development
   */
  debug(message: string, ...args: unknown[]): void {
    if (isDevelopment) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  },

  /**
   * Info level - only in development
   */
  info(message: string, ...args: unknown[]): void {
    if (isDevelopment) {
      console.info(`[INFO] ${message}`, ...args);
    }
  },

  /**
   * Warning level - only in development by default
   */
  warn(message: string, ...args: unknown[]): void {
    if (isDevelopment) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  },

  /**
   * Error level - only in development by default
   * Use options.force for critical errors that should always be logged
   */
  error(message: string, error?: unknown, options?: LoggerOptions): void {
    if (isDevelopment || options?.force) {
      const args: unknown[] = [];
      if (error !== undefined) args.push(error);
      if (options?.context) args.push(options.context);
      console.error(`[ERROR] ${message}`, ...args);
    }
  },

  /**
   * Group logs together (development only)
   */
  group(label: string): void {
    if (isDevelopment) {
      console.group(label);
    }
  },

  /**
   * End log group (development only)
   */
  groupEnd(): void {
    if (isDevelopment) {
      console.groupEnd();
    }
  },

  /**
   * Table output (development only)
   */
  table(data: unknown): void {
    if (isDevelopment) {
      console.table(data);
    }
  },
} as const;

/**
 * Create a scoped logger with a prefix
 */
export function createLogger(scope: string) {
  return {
    debug: (message: string, ...args: unknown[]) =>
      logger.debug(`[${scope}] ${message}`, ...args),
    info: (message: string, ...args: unknown[]) =>
      logger.info(`[${scope}] ${message}`, ...args),
    warn: (message: string, ...args: unknown[]) =>
      logger.warn(`[${scope}] ${message}`, ...args),
    error: (message: string, error?: unknown, options?: LoggerOptions) =>
      logger.error(`[${scope}] ${message}`, error, options),
  } as const;
}
